// OMNI Design System Table Recipe for Chakra UI v3
// Based on OMNI CSS table styles and current react-table implementation

import { defineSlotRecipe } from '@chakra-ui/react';

export const tableRecipe = defineSlotRecipe({
  className: 'omni-table',
  slots: [
    'root', // Main table container
    'header', // Table header container
    'headerGroup', // Header group row
    'headerCell', // Individual header cell
    'resizer', // Column resizer handle
    'body', // Table body container
    'row', // Table row
    'cell', // Table data cell
    'empty', // Empty state container
  ],
  base: {
    root: {
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      fontSize: 'sm',
      fontFamily: 'body',
      color: 'fg.default',
      backgroundColor: 'bg.surface',
      border: '1px solid',
      borderColor: 'border.default',
      borderRadius: 'base',
      boxShadow: 'table-container',
      overflow: 'hidden',
      marginTop: '1',
    },

    header: {
      position: 'sticky',
      top: 0,
      zIndex: 'sticky',
      backgroundColor: 'bg.subtle',
      borderBottom: '2px solid',
      borderBottomColor: 'border.emphasis',
      overflowY: 'auto',
      overflowX: 'hidden',
    },

    headerGroup: {
      display: 'flex',
      flex: 1,
      borderBottom: '1px solid',
      borderBottomColor: 'border.default',
    },

    headerCell: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-start',
      position: 'relative',
      minHeight: '10', // 40px
      padding: 'table-cell-y table-cell-x',
      fontSize: 'sm',
      fontWeight: 'semibold',
      color: 'fg.default',
      backgroundColor: 'transparent',
      borderWidth: 'table-head',
      borderStyle: 'solid',
      borderColor: 'border.subtle',
      textAlign: 'left',
      userSelect: 'none',
      transition: 'all 200ms ease',

      _hover: {
        backgroundColor: 'bg.muted',
      },

      // Sortable header states
      '&[aria-sort="ascending"]': {
        backgroundColor: 'brand.50',
        color: 'brand.700',

        _after: {
          content: '"▲"',
          marginLeft: '2',
          fontSize: '2xs',
          color: 'brand.500',
        },
      },

      '&[aria-sort="descending"]': {
        backgroundColor: 'brand.50',
        color: 'brand.700',

        _after: {
          content: '"▼"',
          marginLeft: '2',
          fontSize: '2xs',
          color: 'brand.500',
        },
      },

      // Right-aligned headers
      '&[data-align="right"]': {
        justifyContent: 'flex-end',
        textAlign: 'right',
      },

      // Center-aligned headers
      '&[data-align="center"]': {
        justifyContent: 'center',
        textAlign: 'center',
      },
    },

    resizer: {
      position: 'absolute',
      top: 0,
      right: 0,
      width: '10px',
      height: '100%',
      zIndex: 1,
      cursor: 'col-resize',
      backgroundColor: 'transparent',
      opacity: 0,
      transition: 'opacity 200ms ease',

      _hover: {
        opacity: 1,
        backgroundColor: 'brand.200',
      },

      _active: {
        opacity: 1,
        backgroundColor: 'brand.400',
      },

      // Touch action for mobile
      touchAction: 'none',
    },

    body: {
      flex: 1,
      overflowY: 'auto',
      overflowX: 'hidden',
      maxHeight: '70vh', // Prevent excessive table height
    },

    row: {
      display: 'flex',
      flex: 1,
      minHeight: '6', // 24px
      lineHeight: '6',
      borderBottom: '1px solid',
      borderBottomColor: 'border.subtle',
      cursor: 'default',
      transition: 'all 150ms ease',

      _hover: {
        backgroundColor: 'bg.subtle',
      },

      _focusVisible: {
        backgroundColor: 'brand.50',
        outline: '2px solid',
        outlineColor: 'brand.200',
        outlineOffset: '-2px',
      },

      // Clickable rows
      '&[data-clickable="true"]': {
        cursor: 'pointer',

        _hover: {
          backgroundColor: 'brand.50',
          borderBottomColor: 'brand.200',
        },

        _active: {
          backgroundColor: 'brand.100',
        },
      },

      // Selected row state
      '&[aria-selected="true"]': {
        backgroundColor: 'brand.100',
        borderBottomColor: 'brand.300',

        _hover: {
          backgroundColor: 'brand.150',
        },
      },

      // Striped rows
      '&:nth-of-type(even)[data-striped="true"]': {
        backgroundColor: 'bg.subtle',
      },

      // Last row
      _last: {
        borderBottomWidth: 0,
      },
    },

    cell: {
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'flex-start',
      padding: 'table-cell-y table-cell-x',
      fontSize: 'sm',
      fontWeight: 'normal',
      color: 'fg.default',
      borderWidth: 'table-cell',
      borderStyle: 'solid',
      borderColor: 'transparent',
      wordBreak: 'break-word',
      overflow: 'hidden',
      textOverflow: 'ellipsis',

      // Right-aligned cells
      '&[data-align="right"]': {
        justifyContent: 'flex-end',
        textAlign: 'right',
      },

      // Center-aligned cells
      '&[data-align="center"]': {
        justifyContent: 'center',
        textAlign: 'center',
      },

      // Numeric cells
      '&[data-type="number"]': {
        justifyContent: 'flex-end',
        textAlign: 'right',
        fontFamily: 'mono',
        fontVariantNumeric: 'tabular-nums',
      },

      // Boolean cells
      '&[data-type="boolean"]': {
        justifyContent: 'center',
        textAlign: 'center',
      },
    },

    empty: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '8',
      fontSize: 'md',
      color: 'fg.muted',
      fontStyle: 'italic',
      minHeight: '32',
      backgroundColor: 'bg.subtle',
    },
  },

  variants: {
    size: {
      xs: {
        root: {
          fontSize: '2xs',
        },
        headerCell: {
          minHeight: '6',
          padding: '1 2',
          fontSize: '2xs',
        },
        cell: {
          padding: '1 2',
          fontSize: '2xs',
        },
        row: {
          minHeight: '5',
          lineHeight: '5',
        },
      },

      sm: {
        root: {
          fontSize: 'xs',
        },
        headerCell: {
          minHeight: '8',
          padding: '1.5 3',
          fontSize: 'xs',
        },
        cell: {
          padding: '1.5 3',
          fontSize: 'xs',
        },
        row: {
          minHeight: '5',
          lineHeight: '5',
        },
      },

      md: {
        root: {
          fontSize: 'sm',
        },
        headerCell: {
          minHeight: '10',
          padding: 'table-cell-y table-cell-x',
          fontSize: 'sm',
        },
        cell: {
          padding: 'table-cell-y table-cell-x',
          fontSize: 'sm',
        },
        row: {
          minHeight: '6',
          lineHeight: '6',
        },
      },

      lg: {
        root: {
          fontSize: 'md',
        },
        headerCell: {
          minHeight: '12',
          padding: '3 5',
          fontSize: 'md',
        },
        cell: {
          padding: '3 5',
          fontSize: 'md',
        },
        row: {
          minHeight: '8',
          lineHeight: '8',
        },
      },

      xl: {
        root: {
          fontSize: 'lg',
        },
        headerCell: {
          minHeight: '14',
          padding: '4 6',
          fontSize: 'lg',
        },
        cell: {
          padding: '4 6',
          fontSize: 'lg',
        },
        row: {
          minHeight: '10',
          lineHeight: '10',
        },
      },
    },

    variant: {
      simple: {
        root: {
          border: 'none',
          boxShadow: 'none',
          backgroundColor: 'transparent',
        },
        headerGroup: {
          borderBottomWidth: '1px',
        },
        row: {
          borderBottomWidth: '1px',
        },
      },

      outline: {
        root: {
          border: '1px solid',
          borderColor: 'border.default',
        },
      },

      striped: {
        row: {
          '&:nth-of-type(even)': {
            backgroundColor: 'bg.subtle',
          },
        },
      },

      elevated: {
        root: {
          boxShadow: 'container-level-two',
          border: '1px solid',
          borderColor: 'border.subtle',
        },
      },

      unstyled: {
        root: {
          border: 'none',
          boxShadow: 'none',
          backgroundColor: 'transparent',
          borderRadius: 'none',
        },
        header: {
          backgroundColor: 'transparent',
          borderBottom: 'none',
        },
        headerGroup: {
          borderBottom: 'none',
        },
        row: {
          borderBottom: 'none',

          _hover: {
            backgroundColor: 'transparent',
          },
        },
        cell: {
          padding: '0',
        },
        headerCell: {
          padding: '0',
          fontWeight: 'normal',
        },
      },
    },

    colorScheme: {
      brand: {
        headerCell: {
          '&[aria-sort]': {
            backgroundColor: 'brand.50',
            color: 'brand.700',
          },
        },
        row: {
          '&[data-clickable="true"]': {
            _hover: {
              backgroundColor: 'brand.50',
            },
          },
          '&[aria-selected="true"]': {
            backgroundColor: 'brand.100',
            borderBottomColor: 'brand.300',
          },
        },
      },

      gray: {
        headerCell: {
          '&[aria-sort]': {
            backgroundColor: 'gray.50',
            color: 'gray.700',
          },
        },
        row: {
          '&[data-clickable="true"]': {
            _hover: {
              backgroundColor: 'gray.50',
            },
          },
          '&[aria-selected="true"]': {
            backgroundColor: 'gray.100',
            borderBottomColor: 'gray.300',
          },
        },
      },
    },
  },

  compoundVariants: [
    {
      variant: 'striped',
      colorScheme: 'brand',
      css: {
        row: {
          '&:nth-of-type(even)': {
            backgroundColor: 'brand.25',
          },
        },
      },
    },

    {
      variant: 'striped',
      colorScheme: 'gray',
      css: {
        row: {
          '&:nth-of-type(even)': {
            backgroundColor: 'gray.25',
          },
        },
      },
    },
  ],

  defaultVariants: {
    size: 'md',
    variant: 'outline',
    colorScheme: 'brand',
  },
});
